import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useLocation } from 'react-router-dom';
import axiosConn from '../../axiosConn';
import { AuthContext } from '../../context/AuthContext';
import {
  HITLAB_SESSION_MESSAGE,
  buildHitLabAuthConfig,
  getHitLabApiMessage,
  getHitLabAttribution,
  isHitLabClientEnabled,
} from './hitLabUtils';
import {
  getHitLabTelemetryContext,
  HITLAB_FUNNEL_EVENTS,
  recordHitLabEvent,
} from './hitLabTelemetry';
import {
  EMPTY_RADIO_QUEUE,
  getActiveRadioTracks,
  mergeRadioCatalogItems,
} from './hitLabRadioTracks';
import HitLabProfileReadyBridge from './HitLabProfileReadyBridge';
import {
  getHitLabPlayerId,
  writeHitLabProfileReadyMarker,
} from './hitLabProfileReady';
import './hitLab.css';

const INITIAL_PROFILE_FORM = {
  artistName: '',
  contactEmail: '',
  contactPhone: '',
  bestContactOrSocialUrl: '',
};

const REQUIRED_PROFILE_FIELDS = [
  'artistName',
];

const PROFILE_LABELS = {
  artistName: 'Artist Name *',
  contactEmail: 'HitLab Contact Email',
  contactPhone: 'Phone Number (optional)',
  bestContactOrSocialUrl: 'Instagram (optional, recommended)',
};

const PROFILE_LIMITS = {
  artistName: 120,
  contactEmail: 254,
  contactPhone: 40,
  bestContactOrSocialUrl: 500,
};

const PROFILE_HELPERS = {
  artistName: 'Use the name you release music under.',
  contactEmail: 'Your Listen Result and HitLab updates will go here.',
  contactPhone:
    'Optional. Used only if there is a real reason to reach you directly about your HitLab submission.',
  bestContactOrSocialUrl:
    'Helps us verify artist context and understand the world around the record.',
};

const PENDING_PAYMENT_RECOVERY_CAMPAIGN = 'hitlab_pending_payment_v1';
const PENDING_PAYMENT_PROFILE_READY_RECOVERY_CAMPAIGN =
  'pending_payment_profile_ready_v2';
const PENDING_PAYMENT_RECOVERY_ACTION = 'remind_later';
const PENDING_PAYMENT_RECOVERY_SOURCE = 'stage2_recovery_prompt';
const PENDING_PAYMENT_RECOVERY_DISMISS_MS = 7 * 24 * 60 * 60 * 1000;
const PENDING_PAYMENT_RECOVERY_REMIND_MS = 30 * 24 * 60 * 60 * 1000;
const DESKTOP_RECOVERY_DWELL_MS = 5000;
const MOBILE_RECOVERY_DWELL_MS = 40000;
const HITLAB_SOURCE_ARTICLE_URL =
  'https://thesource.com/2017/11/30/joe-galaxy-drops-new-visual-drinkin-night-away/';
const HITLAB_ROTATION_COUNT_FLOOR = 12;
const HITLAB_PROCESSING_PACKAGE = 'hitlab_processing';
const HITLAB_PLUS_PACKAGE = 'hitlab_plus_song_ad_rotation';

const DEFAULT_PAYMENT_OPTIONS = [
  {
    packageType: HITLAB_PROCESSING_PACKAGE,
    displayName: 'HitLab Processing',
    amountCents: 999,
    currency: 'usd',
    priceDisplay: '$9.99',
    estimatedAdValueCents: 0,
    adRotationMonths: 0,
  },
  {
    packageType: HITLAB_PLUS_PACKAGE,
    displayName: 'HitLab Plus',
    amountCents: 4999,
    currency: 'usd',
    priceDisplay: '$49.99',
    estimatedAdValueCents: 15000,
    adRotationMonths: 6,
  },
];

const normalizePaymentOptions = (options) => {
  const incoming = Array.isArray(options) ? options : [];
  if (!incoming.length) return DEFAULT_PAYMENT_OPTIONS;
  return incoming
    .map((option) => {
      const defaultOption = DEFAULT_PAYMENT_OPTIONS.find(
        (candidate) => candidate.packageType === option?.packageType,
      );
      return defaultOption ? { ...defaultOption, ...option } : null;
    })
    .filter(Boolean);
};

const findPaymentOption = (options, packageType) =>
  (Array.isArray(options) ? options : []).find(
    (option) => option?.packageType === packageType,
  ) || DEFAULT_PAYMENT_OPTIONS.find((option) => option.packageType === packageType);

const formatStageRadioPreviewTime = (seconds) => {
  const safeSeconds = Math.max(0, Math.floor(Number(seconds) || 0));
  const minutes = Math.floor(safeSeconds / 60);
  const remainder = safeSeconds % 60;
  return `${minutes}:${String(remainder).padStart(2, '0')}`;
};

const isValidEmail = (value) =>
  /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(value || '').trim());

const trimProfileForm = (form) =>
  Object.keys(INITIAL_PROFILE_FORM).reduce((next, key) => {
    const value = String(form[key] || '').trim();
    next[key] = key === 'contactEmail' ? value.toLowerCase() : value;
    return next;
  }, {});

const getProfileFromIntake = (intake = {}) =>
  Object.keys(INITIAL_PROFILE_FORM).reduce((next, key) => {
    next[key] = intake?.[key] || '';
    return next;
  }, {});

const isIntakeProfileComplete = (intake = {}, { needsContactEmail = false } = {}) => {
  const hasSavedProfile = !!String(intake?.artistName || '').trim();
  if (!hasSavedProfile) return false;
  if (needsContactEmail) return isValidEmail(intake?.contactEmail);
  return true;
};

const hasExplicitPaymentIntent = (query, intake = {}) => {
  const step = String(query?.get?.('step') || '').trim().toLowerCase();
  if (step === 'payment' || step === 'checkout') return true;
  const checkout = String(query?.get?.('checkout') || '').trim().toLowerCase();
  if (checkout === 'cancel') return true;
  const src = String(query?.get?.('src') || '').trim().toLowerCase();
  const campaign = String(query?.get?.('campaign') || '').trim();
  if (
    src === 'email' &&
    campaign === PENDING_PAYMENT_PROFILE_READY_RECOVERY_CAMPAIGN
  ) {
    return true;
  }
  return !!intake?.checkoutStartedAt || !!intake?.stripeCheckoutSessionId;
};

const resolveStageForIntake = ({ intake, needsContactEmail, query }) => {
  if (!isIntakeProfileComplete(intake, { needsContactEmail })) return 'profile';
  return hasExplicitPaymentIntent(query, intake) ? 'payment' : 'profileReady';
};

const mergeProfileSavePayload = (current = {}, payload = {}) => {
  const intake = payload.intake || {};
  return {
    ...(current || {}),
    ...intake,
    id: payload.intakeId || intake.id || current?.id,
    artistName:
      payload.artistName ||
      intake.artistName ||
      current?.artistName ||
      '',
    contactEmail:
      payload.contactEmail ||
      intake.contactEmail ||
      current?.contactEmail ||
      '',
    contactPhone:
      payload.contactPhone ||
      intake.contactPhone ||
      current?.contactPhone ||
      '',
    bestContactOrSocialUrl:
      payload.bestContactOrSocialUrl ||
      intake.bestContactOrSocialUrl ||
      current?.bestContactOrSocialUrl ||
      '',
    packageType:
      payload.packageType ||
      intake.packageType ||
      current?.packageType,
    paymentStatus:
      payload.paymentStatus ||
      intake.paymentStatus ||
      current?.paymentStatus,
    status: payload.status || intake.status || current?.status,
  };
};

const isAmbiguousProfileSavePayload = (payload = {}) =>
  !payload.intake &&
  !payload.intakeId &&
  !payload.artistName &&
  !payload.status &&
  !payload.paymentStatus;

const getPlayerEmail = (player) =>
  String(
    player?.data?.player?.emailAddress ||
      player?.data?.player?.email ||
      player?.emailAddress ||
      player?.email ||
      '',
  )
    .trim()
    .toLowerCase();

const getPublicHitLabPlayerId = (player) =>
  String(
    player?.data?.player?.playerId ||
      player?.playerId ||
      player?.data?.player?.username ||
      player?.username ||
      '',
  ).trim();

const safeRecoverySegment = (value) =>
  String(value || '')
    .trim()
    .replace(/[^a-zA-Z0-9_-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 120);

const getRecoveryStorageKey = ({ campaign, intakeId, playerId }) => {
  const identity = safeRecoverySegment(intakeId) || safeRecoverySegment(playerId);
  if (!identity) return '';
  return `hitlab_pending_payment_recovery:${safeRecoverySegment(campaign)}:${identity}`;
};

const readRecoverySuppression = (key) => {
  if (!key || typeof window === 'undefined') return null;
  try {
    const parsed = JSON.parse(window.localStorage.getItem(key) || '');
    if (!parsed || typeof parsed !== 'object') return null;
    const suppressedUntil = Number(parsed.suppressedUntil || 0);
    return Number.isFinite(suppressedUntil) ? parsed : null;
  } catch (err) {
    return null;
  }
};

const isRecoverySuppressed = (key, now = Date.now()) => {
  const parsed = readRecoverySuppression(key);
  return !!parsed && Number(parsed.suppressedUntil || 0) > now;
};

const writeRecoverySuppression = (key, action, durationMs) => {
  if (!key || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      key,
      JSON.stringify({
        campaign: PENDING_PAYMENT_RECOVERY_CAMPAIGN,
        action,
        suppressedUntil: Date.now() + durationMs,
        updatedAt: Date.now(),
      }),
    );
  } catch (err) {
    // Payment must keep working even if storage is unavailable.
  }
};

const isCoarsePointerViewport = () => {
  if (typeof window === 'undefined') return false;
  try {
    return (
      window.matchMedia?.('(pointer: coarse)')?.matches ||
      window.innerWidth <= 860
    );
  } catch (err) {
    return window.innerWidth <= 860;
  }
};

export default function HitLabIntake() {
  const { player } = useContext(AuthContext);
  const location = useLocation();
  const attribution = useMemo(
    () => getHitLabAttribution(location.search),
    [location.search],
  );
  const authConfig = useMemo(
    () => buildHitLabAuthConfig(player),
    [player?.token],
  );
  const [loading, setLoading] = useState(true);
  const [intake, setIntake] = useState(null);
  const [profileForm, setProfileForm] = useState(INITIAL_PROFILE_FORM);
  const [payment, setPayment] = useState({
    amountCents: 999,
    currency: 'usd',
    priceDisplay: '$9.99',
    packageType: HITLAB_PROCESSING_PACKAGE,
  });
  const [paymentOptions, setPaymentOptions] = useState(DEFAULT_PAYMENT_OPTIONS);
  const [selectedPackageType, setSelectedPackageType] = useState(
    HITLAB_PROCESSING_PACKAGE,
  );
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const checkoutInFlightRef = useRef(false);
  const [checkoutStarted, setCheckoutStarted] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const profileSavingRef = useRef(false);
  const [recoveryOpen, setRecoveryOpen] = useState(false);
  const [recoveryReady, setRecoveryReady] = useState(false);
  const [reminderSaving, setReminderSaving] = useState(false);
  const reminderSavingRef = useRef(false);
  const [reminderState, setReminderState] = useState('');
  const recoveryDialogRef = useRef(null);
  const recoveryReturnFocusRef = useRef(null);
  const recoveryViewedRef = useRef(new Set());
  const recoverySessionShownRef = useRef(new Set());
  const profileReadyBridgeViewedRef = useRef(false);
  const stageRadioPreviewAudioRef = useRef(null);
  const stageRadioPreviewActiveRef = useRef(false);
  const [attemptedNext, setAttemptedNext] = useState(false);
  const [stage, setStage] = useState('profile');
  const [paymentRadioTracks, setPaymentRadioTracks] = useState(EMPTY_RADIO_QUEUE);
  const [stageRadioPreviewPlaying, setStageRadioPreviewPlaying] = useState(false);
  const [stageRadioPreviewLoading, setStageRadioPreviewLoading] = useState(false);
  const [stageRadioPreviewError, setStageRadioPreviewError] = useState('');
  const [stageRadioPreviewElapsed, setStageRadioPreviewElapsed] = useState(0);
  const [stageRadioPreviewDuration, setStageRadioPreviewDuration] = useState(0);
  const clientEnabled = isHitLabClientEnabled();
  const query = useMemo(
    () => new URLSearchParams(location.search || ''),
    [location.search],
  );
  const accountEmail = useMemo(() => getPlayerEmail(player), [player]);
  const needsContactEmail = !isValidEmail(accountEmail);
  const currentPlayerId = useMemo(() => getHitLabPlayerId(player), [player]);

  useEffect(() => {
    if (!clientEnabled || query.get('checkout') !== 'cancel') return;
    void recordHitLabEvent(HITLAB_FUNNEL_EVENTS.CHECKOUT_CANCELED, {
      search: location.search,
      path: '/hitlab/intake',
      status: 'pending_payment',
    });
  }, [clientEnabled, location.search, query]);

  useEffect(() => {
    if (!clientEnabled) {
      setLoading(false);
      return undefined;
    }
    if (!authConfig) {
      setError(HITLAB_SESSION_MESSAGE);
      setLoading(false);
      return undefined;
    }
    let active = true;
    axiosConn
      .get('hitlab/intake/me', authConfig)
      .then((response) => {
        if (!active) return;
        const nextIntake = response.data?.data?.intake || null;
        setIntake(nextIntake);
        setProfileForm((current) => ({
          ...current,
          ...getProfileFromIntake(nextIntake),
        }));
        setStage(
          resolveStageForIntake({
            intake: nextIntake,
            needsContactEmail,
            query,
          }),
        );
        if (response.data?.data?.payment) {
          setPayment(response.data.data.payment);
          if (response.data.data.payment.packageType) {
            setSelectedPackageType(response.data.data.payment.packageType);
          }
        }
        if (response.data?.data?.paymentOptions) {
          setPaymentOptions(
            normalizePaymentOptions(response.data.data.paymentOptions),
          );
        }
        void recordHitLabEvent(HITLAB_FUNNEL_EVENTS.INTAKE_VIEWED, {
          search: location.search,
          path: '/hitlab/intake',
        });
      })
      .catch((err) => {
        if (active) {
          setError(
            getHitLabApiMessage(
              err,
              'Artist intake status could not be loaded. Please try again.',
            ),
          );
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [authConfig, clientEnabled, location.search, needsContactEmail, query]);

  const updateField = (event) => {
    const { name, value } = event.target;
    setProfileForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const trimmedProfile = useMemo(
    () => trimProfileForm(profileForm),
    [profileForm],
  );
  const missingRequired = REQUIRED_PROFILE_FIELDS.filter(
    (key) => !trimmedProfile[key],
  );
  const emailReady = !needsContactEmail || isValidEmail(trimmedProfile.contactEmail);
  const profileReady = missingRequired.length === 0 && emailReady;
  const checkoutProfileDraft = useMemo(() => {
    const draft = {
      artistName: trimmedProfile.artistName,
      contactPhone: trimmedProfile.contactPhone,
      bestContactOrSocialUrl: trimmedProfile.bestContactOrSocialUrl,
    };
    if (needsContactEmail) {
      draft.contactEmail = trimmedProfile.contactEmail;
    }
    return draft;
  }, [
    needsContactEmail,
    trimmedProfile.artistName,
    trimmedProfile.bestContactOrSocialUrl,
    trimmedProfile.contactEmail,
    trimmedProfile.contactPhone,
  ]);
  const publicPlayerId = useMemo(() => getPublicHitLabPlayerId(player), [player]);
  const linkedPlayerDisplay = publicPlayerId
    ? `@${publicPlayerId.replace(/^@+/, '')}`
    : 'Your signed-in JG account';
  const showPaymentStep = stage === 'payment' && profileReady;
  const showProfileReadyBridge = stage === 'profileReady' && profileReady;
  const processingPaymentOption = useMemo(
    () => findPaymentOption(paymentOptions, HITLAB_PROCESSING_PACKAGE),
    [paymentOptions],
  );
  const plusPaymentOption = useMemo(
    () => findPaymentOption(paymentOptions, HITLAB_PLUS_PACKAGE),
    [paymentOptions],
  );
  const plusPackageAvailable = useMemo(
    () =>
      (Array.isArray(paymentOptions) ? paymentOptions : []).some(
        (option) => option?.packageType === HITLAB_PLUS_PACKAGE,
      ),
    [paymentOptions],
  );
  const paymentPriceDisplay = processingPaymentOption?.priceDisplay || '$9.99';
  const plusPriceDisplay = plusPaymentOption?.priceDisplay || '$49.99';
  const savedPaymentArtistName = String(intake?.artistName || '').trim();
  const paymentHeading = savedPaymentArtistName
    ? `${savedPaymentArtistName}, your JG HitLab Artist Profile is ready.`
    : 'Your JG HitLab Artist Profile is ready.';
  const paid =
    intake?.paymentStatus === 'paid' || intake?.status === 'paid_pending_upload';
  const checkoutBlocked =
    intake &&
    !paid &&
    intake.status &&
    intake.status !== 'pending_payment';
  const activeIntakeId = String(intake?.id || intake?._id || '').trim();
  const paymentPending =
    intake?.status === 'pending_payment' && intake?.paymentStatus === 'pending';
  const hasCheckoutStarted =
    checkoutStarted || !!intake?.checkoutStartedAt || !!intake?.stripeCheckoutSessionId;
  const recoveryStorageKey = useMemo(
    () =>
      getRecoveryStorageKey({
        campaign: PENDING_PAYMENT_RECOVERY_CAMPAIGN,
        intakeId: activeIntakeId,
        playerId: currentPlayerId,
      }),
    [activeIntakeId, currentPlayerId],
  );
  const recoveryEligible =
    !!authConfig &&
    showPaymentStep &&
    !!intake &&
    paymentPending &&
    !busy &&
    !hasCheckoutStarted &&
    !!recoveryStorageKey;

  useEffect(() => {
    if (!showProfileReadyBridge || profileReadyBridgeViewedRef.current) return;
    profileReadyBridgeViewedRef.current = true;
    void recordHitLabEvent(HITLAB_FUNNEL_EVENTS.PROFILE_READY_BRIDGE_VIEWED, {
      search: location.search,
      path: '/hitlab/intake',
      status: intake?.status || 'pending_payment',
    });
  }, [intake?.status, location.search, showProfileReadyBridge]);

  const paymentRadioExamples = useMemo(
    () => paymentRadioTracks.slice(0, 3),
    [paymentRadioTracks],
  );
  const stageRadioPreviewTrack = useMemo(
    () => paymentRadioExamples.find((track) => track?.audioUrl) || null,
    [paymentRadioExamples],
  );
  const showStageRadioPreview = !!stageRadioPreviewTrack?.audioUrl;
  const stageRadioPreviewProgress =
    stageRadioPreviewDuration > 0
      ? Math.min(
          100,
          Math.max(0, (stageRadioPreviewElapsed / stageRadioPreviewDuration) * 100),
        )
      : 0;
  const rotationCountLine =
    paymentRadioTracks.length >= HITLAB_ROTATION_COUNT_FLOOR
      ? `${HITLAB_ROTATION_COUNT_FLOOR}+ records in rotation.`
      : 'Records are in rotation.';

  useEffect(() => {
    if (!showPaymentStep) return undefined;
    let active = true;
    axiosConn
      .get('hitlab/radio/catalog')
      .then((response) => {
        if (!active) return;
        const mergedQueue = mergeRadioCatalogItems(response?.data?.data?.items);
        const activeTracks = getActiveRadioTracks(mergedQueue);
        setPaymentRadioTracks(activeTracks);
      })
      .catch(() => {
        if (active) {
          setPaymentRadioTracks((current) =>
            current && current.length ? current : EMPTY_RADIO_QUEUE,
          );
        }
      });
    return () => {
      active = false;
    };
  }, [showPaymentStep]);

  useEffect(() => {
    stageRadioPreviewActiveRef.current = true;
    return () => {
      stageRadioPreviewActiveRef.current = false;
      const audio = stageRadioPreviewAudioRef.current;
      if (audio) audio.pause();
    };
  }, []);

  useEffect(() => {
    const audio = stageRadioPreviewAudioRef.current;
    if (audio) {
      audio.pause();
      try {
        audio.currentTime = 0;
      } catch {
        // Some media shims expose currentTime as read-only.
      }
    }
    setStageRadioPreviewPlaying(false);
    setStageRadioPreviewLoading(false);
    setStageRadioPreviewError('');
    setStageRadioPreviewElapsed(0);
    setStageRadioPreviewDuration(0);
  }, [showPaymentStep, stageRadioPreviewTrack?.id]);

  const handleStageRadioPreviewLoadedMetadata = () => {
    if (!stageRadioPreviewActiveRef.current) return;
    const nextDuration = Number(stageRadioPreviewAudioRef.current?.duration);
    setStageRadioPreviewDuration(Number.isFinite(nextDuration) ? nextDuration : 0);
  };

  const handleStageRadioPreviewTimeUpdate = () => {
    if (!stageRadioPreviewActiveRef.current) return;
    const nextElapsed = Number(stageRadioPreviewAudioRef.current?.currentTime);
    setStageRadioPreviewElapsed(Number.isFinite(nextElapsed) ? nextElapsed : 0);
  };

  const handleStageRadioPreviewEnded = () => {
    const audio = stageRadioPreviewAudioRef.current;
    if (audio) {
      try {
        audio.currentTime = 0;
      } catch {
        // Some media shims expose currentTime as read-only.
      }
    }
    if (!stageRadioPreviewActiveRef.current) return;
    setStageRadioPreviewPlaying(false);
    setStageRadioPreviewLoading(false);
    setStageRadioPreviewElapsed(0);
  };

  const handleStageRadioPreviewError = () => {
    if (!stageRadioPreviewActiveRef.current) return;
    setStageRadioPreviewError(
      'Preview unavailable. You can still continue to secure payment.',
    );
    setStageRadioPreviewPlaying(false);
    setStageRadioPreviewLoading(false);
  };

  const toggleStageRadioPreview = async () => {
    if (!showStageRadioPreview) return;
    const audio = stageRadioPreviewAudioRef.current;
    if (!audio) return;
    if (stageRadioPreviewPlaying) {
      audio.pause();
      setStageRadioPreviewPlaying(false);
      setStageRadioPreviewLoading(false);
      return;
    }
    setStageRadioPreviewError('');
    setStageRadioPreviewLoading(true);
    audio.pause();
    try {
      audio.currentTime = 0;
      setStageRadioPreviewElapsed(0);
    } catch {
      // Some media shims expose currentTime as read-only.
    }
    try {
      await audio.play();
      if (!stageRadioPreviewActiveRef.current) return;
      setStageRadioPreviewPlaying(true);
    } catch {
      if (!stageRadioPreviewActiveRef.current) return;
      setStageRadioPreviewError(
        'Preview unavailable. You can still continue to secure payment.',
      );
      setStageRadioPreviewPlaying(false);
    } finally {
      if (stageRadioPreviewActiveRef.current) {
        setStageRadioPreviewLoading(false);
      }
    }
  };

  const continueToPayment = async (event) => {
    event.preventDefault();
    if (profileSavingRef.current) return;
    setAttemptedNext(true);
    setError('');
    if (!authConfig) {
      setError(HITLAB_SESSION_MESSAGE);
      return;
    }
    if (!profileReady) {
      setError('Complete your artist profile before continuing.');
      return;
    }
    profileSavingRef.current = true;
    setProfileSaving(true);
    try {
      const response = await axiosConn.post(
        'hitlab/intake/profile',
        {
          attribution,
          profileDraft: checkoutProfileDraft,
          telemetry: getHitLabTelemetryContext(),
        },
        authConfig,
      );
      const payload = response.data?.data || {};
      let nextIntake = mergeProfileSavePayload(intake, payload);
      if (isAmbiguousProfileSavePayload(payload)) {
        const statusResponse = await axiosConn.get('hitlab/intake/me', authConfig);
        const statusPayload = statusResponse.data?.data || {};
        nextIntake = statusPayload.intake || nextIntake;
        if (statusPayload.payment) setPayment(statusPayload.payment);
        if (statusPayload.paymentOptions) {
          setPaymentOptions(normalizePaymentOptions(statusPayload.paymentOptions));
        }
      }
      setIntake(nextIntake);
      if (payload.payment) setPayment(payload.payment);
      if (payload.paymentOptions) {
        setPaymentOptions(normalizePaymentOptions(payload.paymentOptions));
      }
      if (!isIntakeProfileComplete(nextIntake, { needsContactEmail })) {
        setStage('profile');
        setError(
          'Artist profile was saved, but profile completion could not be confirmed. Please review and continue.',
        );
        return;
      }
      if (hasExplicitPaymentIntent(query, nextIntake)) {
        setStage('payment');
        return;
      }
      writeHitLabProfileReadyMarker(currentPlayerId);
      setStage('profileReady');
    } catch (err) {
      setError(
        getHitLabApiMessage(
          err,
          'Artist profile could not be saved. Please try again.',
        ),
      );
    } finally {
      profileSavingRef.current = false;
      setProfileSaving(false);
    }
  };

  const startCheckout = async (packageTypeValue) => {
    const packageType =
      typeof packageTypeValue === 'string'
        ? packageTypeValue
        : selectedPackageType;
    setSelectedPackageType(packageType);
    setError('');
    if (!authConfig) {
      setError(HITLAB_SESSION_MESSAGE);
      return;
    }
    if (!profileReady) {
      setStage('profile');
      setAttemptedNext(true);
      setError('Complete your artist profile before continuing.');
      return;
    }
    if (busy || checkoutInFlightRef.current) return;
    checkoutInFlightRef.current = true;
    setBusy(true);
    try {
      const response = await axiosConn.post(
        'hitlab/intake/checkout',
        {
          attribution,
          packageType,
          telemetry: getHitLabTelemetryContext(),
        },
        authConfig,
      );
      const payload = response.data?.data || {};
      if (payload.intake) setIntake(payload.intake);
      if (payload.payment) {
        setPayment(payload.payment);
        if (payload.payment.packageType) {
          setSelectedPackageType(payload.payment.packageType);
        }
      }
      if (payload.paymentOptions) {
        setPaymentOptions(normalizePaymentOptions(payload.paymentOptions));
      }
      if (payload.checkoutUrl) {
        setCheckoutStarted(true);
        window.location.assign(payload.checkoutUrl);
        return;
      }
      if (
        payload.alreadyPaid ||
        payload.intake?.paymentStatus === 'paid' ||
        payload.intake?.status === 'paid_pending_upload'
      ) {
        checkoutInFlightRef.current = false;
        setBusy(false);
        return;
      }
      throw new Error('missing-checkout-url');
    } catch (err) {
      checkoutInFlightRef.current = false;
      setError(
        getHitLabApiMessage(
          err,
          'Checkout could not be started. Please try again.',
        ),
      );
      setBusy(false);
    }
  };

  const recordRecoveryEvent = (eventName, { dedupe = false } = {}) => {
    void recordHitLabEvent(eventName, {
      search: location.search,
      path: '/hitlab/intake',
      status: 'pending_payment',
      dedupe,
    });
  };

  const recordProfileReadyEvent = (eventName) => {
    void recordHitLabEvent(eventName, {
      search: location.search,
      path: '/hitlab/intake',
      status: intake?.status || 'pending_payment',
      dedupe: false,
    });
  };

  const continuePaymentFromProfileReady = () => {
    recordProfileReadyEvent(HITLAB_FUNNEL_EVENTS.PROFILE_READY_PAYMENT_CLICKED);
    setStage('payment');
  };

  const recordProfileReadyUniverseClick = () => {
    recordProfileReadyEvent(HITLAB_FUNNEL_EVENTS.PROFILE_READY_UNIVERSE_CLICKED);
  };

  const openRecoveryPrompt = () => {
    if (!recoveryEligible || recoveryOpen) return;
    if (!recoveryReady && !isCoarsePointerViewport()) return;
    if (isRecoverySuppressed(recoveryStorageKey)) return;
    if (recoverySessionShownRef.current.has(recoveryStorageKey)) return;
    recoverySessionShownRef.current.add(recoveryStorageKey);
    recoveryReturnFocusRef.current =
      typeof document !== 'undefined' ? document.activeElement : null;
    setReminderState('');
    setRecoveryOpen(true);
    if (!recoveryViewedRef.current.has(recoveryStorageKey)) {
      recoveryViewedRef.current.add(recoveryStorageKey);
      recordRecoveryEvent(HITLAB_FUNNEL_EVENTS.PENDING_PAYMENT_RECOVERY_VIEWED);
    }
  };

  const dismissRecoveryPrompt = () => {
    writeRecoverySuppression(
      recoveryStorageKey,
      'dismissed',
      PENDING_PAYMENT_RECOVERY_DISMISS_MS,
    );
    setRecoveryOpen(false);
    recordRecoveryEvent(HITLAB_FUNNEL_EVENTS.PENDING_PAYMENT_RECOVERY_DISMISSED);
  };

  const continuePaymentFromRecovery = async () => {
    if (busy || checkoutInFlightRef.current) return;
    recordRecoveryEvent(
      HITLAB_FUNNEL_EVENTS.PENDING_PAYMENT_RECOVERY_CONTINUE_PAYMENT_CLICKED,
    );
    setRecoveryOpen(false);
    await startCheckout(HITLAB_PROCESSING_PACKAGE);
  };

  const remindLaterFromRecovery = async () => {
    if (!authConfig || reminderSavingRef.current) return;
    reminderSavingRef.current = true;
    setReminderSaving(true);
    setReminderState('');
    recordRecoveryEvent(
      HITLAB_FUNNEL_EVENTS.PENDING_PAYMENT_RECOVERY_REMIND_LATER_CLICKED,
    );
    try {
      const response = await axiosConn.post(
        'hitlab/intake/pending-payment-recovery',
        {
          campaign: PENDING_PAYMENT_RECOVERY_CAMPAIGN,
          action: PENDING_PAYMENT_RECOVERY_ACTION,
          source: PENDING_PAYMENT_RECOVERY_SOURCE,
          telemetry: getHitLabTelemetryContext(),
        },
        authConfig,
      );
      const payload = response.data?.data || {};
      if (payload.pendingPaymentRecovery) {
        setIntake((current) => ({
          ...(current || {}),
          pendingPaymentRecovery: payload.pendingPaymentRecovery,
        }));
      }
      writeRecoverySuppression(
        recoveryStorageKey,
        'remind_later',
        PENDING_PAYMENT_RECOVERY_REMIND_MS,
      );
      setReminderState('success');
      recordRecoveryEvent(
        HITLAB_FUNNEL_EVENTS.PENDING_PAYMENT_RECOVERY_REMIND_LATER_SAVED,
      );
    } catch (err) {
      setReminderState('error');
    } finally {
      reminderSavingRef.current = false;
      setReminderSaving(false);
    }
  };

  const recordBackToHitLab = () => {
    recordRecoveryEvent(
      HITLAB_FUNNEL_EVENTS.PENDING_PAYMENT_RECOVERY_BACK_TO_HITLAB_CLICKED,
    );
  };

  useEffect(() => {
    if (!recoveryEligible || isRecoverySuppressed(recoveryStorageKey)) {
      setRecoveryReady(false);
      return undefined;
    }
    const timer = window.setTimeout(() => {
      setRecoveryReady(true);
    }, DESKTOP_RECOVERY_DWELL_MS);
    return () => window.clearTimeout(timer);
  }, [recoveryEligible, recoveryStorageKey]);

  useEffect(() => {
    if (!recoveryEligible || !isCoarsePointerViewport()) return undefined;
    if (isRecoverySuppressed(recoveryStorageKey)) return undefined;
    const timer = window.setTimeout(() => {
      openRecoveryPrompt();
    }, MOBILE_RECOVERY_DWELL_MS);
    return () => window.clearTimeout(timer);
  }, [recoveryEligible, recoveryStorageKey, recoveryOpen]);

  useEffect(() => {
    if (!recoveryEligible || !recoveryReady || isCoarsePointerViewport()) {
      return undefined;
    }
    const handleExitIntent = (event) => {
      if (event.relatedTarget) return;
      if (Number(event.clientY) <= 12) openRecoveryPrompt();
    };
    document.addEventListener('mouseout', handleExitIntent);
    return () => document.removeEventListener('mouseout', handleExitIntent);
  }, [recoveryEligible, recoveryReady, recoveryStorageKey, recoveryOpen]);

  useEffect(() => {
    if (!recoveryOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusTimer = window.setTimeout(() => {
      recoveryDialogRef.current?.focus();
    }, 0);
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') dismissRecoveryPrompt();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
      const target = recoveryReturnFocusRef.current;
      if (target && typeof target.focus === 'function') {
        target.focus();
      }
    };
  }, [recoveryOpen]);

  const renderField = (
    name,
    {
      textarea = false,
      required = false,
      type = 'text',
      placeholder = '',
      helper = PROFILE_HELPERS[name],
    } = {},
  ) => {
    const inputProps = {
      name,
      value: profileForm[name],
      onChange: updateField,
      maxLength: PROFILE_LIMITS[name],
      required,
      placeholder,
    };
    return (
      <label className={textarea ? 'hitlab-form-wide' : undefined}>
        {PROFILE_LABELS[name]}
        {textarea ? (
          <textarea {...inputProps} />
        ) : (
          <input type={type} {...inputProps} />
        )}
        {helper ? <span className="hitlab-field-helper">{helper}</span> : null}
      </label>
    );
  };

  return (
    <main className="hitlab-page hitlab-workflow">
      <Helmet>
        <title>JG HitLab Intake | Artist Profile</title>
      </Helmet>
      <section className="hitlab-shell hitlab-workflow-grid">
        <div className="hitlab-workflow-copy">
          <p className="hitlab-kicker">
            {showPaymentStep || showProfileReadyBridge
              ? 'ARTIST PROFILE READY'
              : 'ARTIST PROFILE'}
          </p>
          <h1>
            {showPaymentStep
              ? paymentHeading
              : showProfileReadyBridge
                ? 'Enter JG Universe before your record enters review.'
                : 'Create Your JG Artist Profile'}
          </h1>
          <p className="hitlab-lede">
            {showPaymentStep
              ? 'Now put one record into the HitLab review path.'
              : showProfileReadyBridge
                ? 'Your profile is saved. Payment is available when you are ready, and JG Universe is open now.'
                : 'Create the artist identity Joe Galaxy will use to review your HitLab path. This is not a public profile yet - it helps us know who is behind the record before anything enters review.'}
          </p>
          {showPaymentStep ? (
            <p className="hitlab-payment-profile-utility">
              Need to update your artist profile?{' '}
              <button type="button" onClick={() => setStage('profile')}>
                Edit profile.
              </button>
            </p>
          ) : null}

          {query.get('checkout') === 'cancel' ? (
            <div className="hitlab-alert">That step was canceled. Your profile is saved; you can continue when ready.</div>
          ) : null}
          {error ? <div className="hitlab-alert hitlab-alert-error">{error}</div> : null}
          {!clientEnabled ? (
            <div className="hitlab-alert">Artist intake is not accepting new submissions right now.</div>
          ) : loading ? (
            <div className="hitlab-alert">Loading artist profile...</div>
          ) : !authConfig ? (
            null
          ) : paid ? (
            <Link className="hitlab-primary-link" to="/hitlab/submit">
              Continue to song submission
            </Link>
          ) : checkoutBlocked ? (
            <div className="hitlab-alert">
              Your artist intake is already in progress. Current status: {intake?.status || 'in review'}.
            </div>
          ) : showPaymentStep ? (
            <section className="hitlab-form hitlab-payment-stage" aria-label="Artist intake processing">
              <section
                className="hitlab-package-selector"
                aria-label="Choose HitLab package"
              >
                <article
                  className={[
                    'hitlab-package-card',
                    selectedPackageType === HITLAB_PROCESSING_PACKAGE
                      ? 'is-selected'
                      : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <span>Base</span>
                  <h2>HitLab Processing</h2>
                  <strong>{paymentPriceDisplay}</strong>
                  <p>
                    One original record enters the HitLab processing path:
                    file handling, rights screening, review queue entry, and a
                    JG HitLab Listen Result after it is processed and heard.
                  </p>
                  <button
                    type="button"
                    className="hitlab-primary-btn"
                    disabled={busy}
                    onClick={() => startCheckout(HITLAB_PROCESSING_PACKAGE)}
                  >
                    {busy
                      ? 'Opening secure payment...'
                      : `Continue with Processing - ${paymentPriceDisplay}`}
                  </button>
                </article>
                {plusPackageAvailable ? (
                  <article
                    className={[
                      'hitlab-package-card',
                      'hitlab-package-card-plus',
                      selectedPackageType === HITLAB_PLUS_PACKAGE
                        ? 'is-selected'
                        : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    <span>Plus</span>
                    <h2>HitLab Plus</h2>
                    <strong>{plusPriceDisplay}</strong>
                    <p>
                      Includes HitLab Processing plus 6 months of JG Universe
                      Song Ad Rotation eligibility and $150 Spotlight Ads value.
                    </p>
                    <ul>
                      <li>Song Ad Rotation is advertising.</li>
                      <li>Radio Rotation must still be earned.</li>
                      <li>Placement cannot be bought.</li>
                    </ul>
                    <button
                      type="button"
                      className="hitlab-primary-btn"
                      disabled={busy}
                      onClick={() => startCheckout(HITLAB_PLUS_PACKAGE)}
                    >
                      {busy
                        ? 'Opening secure payment...'
                        : `Upgrade to HitLab Plus - ${plusPriceDisplay}`}
                    </button>
                  </article>
                ) : null}
              </section>
              <section className="hitlab-payment-proof-card" aria-label="Proof before payment">
                <p className="hitlab-kicker">PROOF BEFORE PAYMENT</p>
                <h2>This is not an empty submission form.</h2>
                <p>
                  Joe Galaxy Radio is live, records are in rotation, and every
                  valid submission receives a JG HitLab Listen Result after it
                  is processed and heard.
                </p>
                <ul className="hitlab-payment-proof-chips" aria-label="HitLab proof points">
                  <li>Featured in The Source</li>
                  <li>Radio Rotation is live</li>
                  <li>Listen Result included</li>
                  <li>One record only</li>
                </ul>
                <a
                  className="hitlab-payment-source-link"
                  href={HITLAB_SOURCE_ARTICLE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open The Source article about Joe Galaxy in a new tab"
                >
                  Joe Galaxy Drops New Visual For “Drinkin The Night Away”{' '}
                  <span aria-hidden="true">↗</span>
                </a>
              </section>
              <section className="hitlab-payment-radio-proof" aria-label="Joe Galaxy Radio proof">
                <h2>Radio Rotation is moving.</h2>
                <p>
                  Approved records are live inside Joe Galaxy Radio Rotation.
                </p>
                {paymentRadioExamples.length ? (
                  <div className="hitlab-payment-rotation-list">
                    <p>Recently in rotation</p>
                    <ul>
                      {paymentRadioExamples.map((track) => (
                        <li key={track.id}>
                          <span>{track.songTitle || track.title}</span>
                          <small>{track.artistName}</small>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                <p className="hitlab-payment-rotation-count">
                  {rotationCountLine}
                </p>
                {showStageRadioPreview ? (
                  <div
                    className="hitlab-payment-radio-preview"
                    aria-label="Stage 2 Radio preview"
                  >
                    <div className="hitlab-payment-radio-preview-now">
                      {stageRadioPreviewTrack.coverArtUrl ? (
                        <img
                          src={stageRadioPreviewTrack.coverArtUrl}
                          alt={`Cover art for ${stageRadioPreviewTrack.songTitle} by ${stageRadioPreviewTrack.artistName}`}
                        />
                      ) : (
                        <div
                          className="hitlab-payment-radio-preview-orbit"
                          aria-hidden="true"
                        >
                          <span />
                          <strong>JG</strong>
                        </div>
                      )}
                      <div className="hitlab-payment-radio-preview-copy">
                        <span>Radio Preview</span>
                        <strong>
                          {stageRadioPreviewTrack.songTitle ||
                            stageRadioPreviewTrack.title}
                        </strong>
                        <small>{stageRadioPreviewTrack.artistName}</small>
                      </div>
                      <div
                        className={`hitlab-payment-radio-preview-bars ${
                          stageRadioPreviewPlaying ? 'is-playing' : ''
                        }`}
                        aria-hidden="true"
                      >
                        <span />
                        <span />
                        <span />
                        <span />
                      </div>
                    </div>
                    <audio
                      ref={stageRadioPreviewAudioRef}
                      src={stageRadioPreviewTrack.audioUrl}
                      preload="metadata"
                      onLoadedMetadata={handleStageRadioPreviewLoadedMetadata}
                      onTimeUpdate={handleStageRadioPreviewTimeUpdate}
                      onEnded={handleStageRadioPreviewEnded}
                      onError={handleStageRadioPreviewError}
                      aria-label="HitLab Stage 2 Radio preview audio"
                    />
                    <div className="hitlab-payment-radio-preview-progress-row">
                      <span>{formatStageRadioPreviewTime(stageRadioPreviewElapsed)}</span>
                      <div
                        className="hitlab-payment-radio-preview-progress"
                        role="progressbar"
                        aria-label="Stage 2 Radio preview progress"
                        aria-valuemin="0"
                        aria-valuemax="100"
                        aria-valuenow={Math.round(stageRadioPreviewProgress)}
                      >
                        <span style={{ width: `${stageRadioPreviewProgress}%` }} />
                      </div>
                      <span>{formatStageRadioPreviewTime(stageRadioPreviewDuration)}</span>
                    </div>
                    {stageRadioPreviewError ? (
                      <p className="hitlab-payment-radio-error" role="alert">
                        {stageRadioPreviewError}
                      </p>
                    ) : null}
                    <div className="hitlab-payment-radio-actions">
                      <button
                        type="button"
                        className="hitlab-secondary-btn"
                        onClick={toggleStageRadioPreview}
                        aria-label={
                          stageRadioPreviewPlaying
                            ? 'Pause Radio preview'
                            : 'Play Radio preview'
                        }
                      >
                        {stageRadioPreviewLoading
                          ? 'Loading'
                          : stageRadioPreviewPlaying
                            ? 'Pause Preview'
                            : 'Play Radio Preview'}
                      </button>
                    </div>
                  </div>
                ) : null}
                <p className="hitlab-payment-earned-line">
                  If your record earns it, this is the lane it can enter.
                </p>
              </section>
              <section className="hitlab-payment-detail" aria-label="What HitLab processing covers">
                <h2>What HitLab Processing Covers</h2>
                <p>
                  Processing covers file handling, rights screening, review
                  queue entry, and a focused listen from Joe Galaxy and selected
                  ears.
                </p>
                <ul className="hitlab-payment-coverage-list">
                  <li>Your record enters the HitLab review queue.</li>
                  <li>Your rights confirmations are screened.</li>
                  <li>Your valid submission receives a JG HitLab Listen Result.</li>
                  <li>
                    If the record earns it, it can move toward the next
                    Radio/HitLab opportunity.
                  </li>
                </ul>
              </section>
              <div className="hitlab-payment-close" aria-label="HitLab payment boundary">
                <p>Payment gets your record processed.</p>
                <p>Ad rotation is advertising.</p>
                <p>Excellence earns the next move.</p>
                <p>Placement cannot be bought.</p>
              </div>
              <p className="hitlab-payment-support-line">
                After payment, you'll submit your private song link and rights
                confirmations.
              </p>
            </section>
          ) : showProfileReadyBridge ? (
            <HitLabProfileReadyBridge
              artistName={savedPaymentArtistName}
              busy={busy}
              onContinuePayment={continuePaymentFromProfileReady}
              onEditProfile={() => setStage('profile')}
              onUniverseClick={recordProfileReadyUniverseClick}
              paymentPriceDisplay={paymentPriceDisplay}
            />
          ) : (
            <form className="hitlab-form hitlab-profile-form" onSubmit={continueToPayment} noValidate>
              <div className="hitlab-form-grid">
                {renderField('artistName', {
                  required: true,
                  placeholder: 'Artist or stage name',
                })}
                {needsContactEmail ? (
                  renderField('contactEmail', { required: true, type: 'email' })
                ) : (
                  <div className="hitlab-readonly-field">
                    <span>HitLab Contact Email</span>
                    <strong>{accountEmail}</strong>
                    <span className="hitlab-field-helper">
                      {PROFILE_HELPERS.contactEmail}
                    </span>
                  </div>
                )}
                {renderField('contactPhone', {
                  placeholder: 'Best phone number',
                })}
                {renderField('bestContactOrSocialUrl', {
                  placeholder: '@artistname or Instagram URL',
                })}
                <div className="hitlab-readonly-field">
                  <span>Linked JG Player</span>
                  <strong>Linked JG Player: {linkedPlayerDisplay}</strong>
                </div>
              </div>

              {attemptedNext && !profileReady ? (
                <div className="hitlab-alert hitlab-alert-error">
                  Required fields: artist name
                  {needsContactEmail ? ' and contact email.' : '.'}
                </div>
              ) : null}

              <button
                type="submit"
                className="hitlab-primary-btn"
                disabled={profileSaving}
              >
                {profileSaving ? 'Saving...' : 'Create Artist Profile'}
              </button>
            </form>
          )}
        </div>
        <aside className={`hitlab-checkout-panel ${
          showPaymentStep || showProfileReadyBridge
            ? 'hitlab-checkout-panel-active'
            : ''
        }`}>
          <span>
            {showPaymentStep
              ? 'RECORD FIRST'
              : showProfileReadyBridge
                ? 'UNIVERSE FIRST'
                : 'PROFILE FIRST'}
          </span>
          {showPaymentStep ? (
            <>
              <h2>The record still decides.</h2>
              <p className="hitlab-panel-copy">
                One original record enters the HitLab review path. Any bigger
                move has to be earned by the music.
              </p>
            </>
          ) : showProfileReadyBridge ? (
            <>
              <h2>Profile saved. Payment has not started.</h2>
              <p className="hitlab-panel-copy">
                Enter JG Universe now or continue to the existing payment step.
                Stripe Checkout only starts after you choose secure payment.
              </p>
            </>
          ) : (
            <>
              <h2>Create your HitLab artist profile.</h2>
              <p className="hitlab-panel-copy">
                One required field keeps the intake moving. Song details come
                after this profile step.
              </p>
            </>
          )}
        </aside>
      </section>
      {recoveryOpen ? (
        <div
          className="hitlab-recovery-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) dismissRecoveryPrompt();
          }}
        >
          <section
            className="hitlab-recovery-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="hitlab-recovery-title"
            tabIndex="-1"
            ref={recoveryDialogRef}
          >
            <button
              type="button"
              className="hitlab-recovery-close"
              aria-label="Close pending payment reminder"
              onClick={dismissRecoveryPrompt}
            >
              Close
            </button>
            <p className="hitlab-kicker">PROFILE SAVED</p>
            <h2 id="hitlab-recovery-title">
              {savedPaymentArtistName
                ? `${savedPaymentArtistName}, your profile is saved - but your record is not in review yet.`
                : 'Your profile is saved - but your record is not in review yet.'}
            </h2>
            <p className="hitlab-recovery-body">
              Your profile is saved, but your record is not in review yet. One
              step keeps the intake moving. Secure payment locks in processing
              so your record can enter the HitLab review path.
            </p>
            <p className="hitlab-recovery-body">
              A JG HitLab Listen Result can only happen after your valid
              submission is processed and heard.
            </p>
            <p className="hitlab-recovery-body">
              HitLab is building the paths serious records need: Joe Galaxy
              Radio Rotation, Hit Radar, listener feedback, tips, direct song
              buys, Instagram discovery, and LocalOps promo opportunities.
            </p>
            <div className="hitlab-payment-close" aria-label="HitLab recovery boundary">
              <p>Payment gets your record processed.</p>
              <p>Excellence earns the next move.</p>
              <p>Placement cannot be bought.</p>
            </div>
            <div className="hitlab-recovery-actions">
              <button
                type="button"
                className="hitlab-primary-btn"
                disabled={busy}
                onClick={continuePaymentFromRecovery}
              >
                {busy
                  ? 'Opening secure payment...'
                  : `Continue to Secure Payment - ${paymentPriceDisplay}`}
              </button>
              <button
                type="button"
                className="hitlab-secondary-btn"
                disabled={reminderSaving}
                onClick={remindLaterFromRecovery}
              >
                {reminderSaving ? 'Saving reminder...' : 'Remind me later'}
              </button>
              <Link
                className="hitlab-secondary-btn"
                to="/hitlab"
                onClick={recordBackToHitLab}
              >
                Back to HitLab
              </Link>
            </div>
            <p className="hitlab-recovery-note">
              Reminder uses your HitLab account email. No email is sent from
              this step.
            </p>
            {reminderState === 'success' ? (
              <div className="hitlab-alert">
                Reminder saved. We'll use your HitLab account email when HitLab
                reminders launch.
              </div>
            ) : null}
            {reminderState === 'error' ? (
              <div className="hitlab-alert hitlab-alert-error">
                Reminder could not be saved. You can still continue to secure
                payment.
              </div>
            ) : null}
          </section>
        </div>
      ) : null}
    </main>
  );
}
